import { dateToString } from "src/utils/date";
import capitalize from "lodash/capitalize";
import { planMetadata } from "src/services/plans";
import { misc } from "@k8slens/lds-icons";

import { RepeatIcon } from "src/components/Icon/Navigation";
import Button from "src/components/Button/TrackedButton";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";

import { ActiveLensSubscription } from "./ActiveLensSubscription";
import { License, LicenseType, SubscriptionSeatWithBillingPeriod } from "./license";

import styles from "./ProSubscription.module.css";
import { useGetLicense } from "src/hooks/useGetLicense";
import { BillingInfo, SubscriptionSeat } from "lens-platform-sdk";
import { useMemo } from "react";

const { CreditCardIcon, EventIcon } = misc;

const getBillingPeriodLabel = (
  license: ReturnType<typeof useGetLicense>,
  currentSubscriptionSeatWithBillingPeriod: SubscriptionSeatWithBillingPeriod | undefined,
  isCanceled: boolean,
) => {
  if (license?.type === LicenseType.proTrial) {
    return "No Automatic Renewal";
  }

  if (currentSubscriptionSeatWithBillingPeriod?.deactivatedAt !== null) {
    return "Deactivated subscription";
  }

  const label = isCanceled ? "Cancelled Subscription" : "Automatic Renewal";

  return `${label} (${capitalize(currentSubscriptionSeatWithBillingPeriod?.billingPeriod ?? "")})`;
};

const getBillingPeriodEndsLabel = (
  license: ReturnType<typeof useGetLicense>,
  currentSubscriptionSeatWithBillingPeriod: SubscriptionSeatWithBillingPeriod | undefined,
  isCanceled: boolean,
) => {
  if (license?.type === LicenseType.proTrial) {
    return "Trial Ends";
  }

  return isCanceled || currentSubscriptionSeatWithBillingPeriod?.deactivatedAt !== null
    ? "Valid Until"
    : "Next Renewal";
};

interface Props {
  license: ReturnType<typeof useGetLicense>;
  onLicenseChange?: () => void;
  onCancelSubscription?: (license: License) => void;
  billingInfoUrl?: string;
  accountUrl?: string;
  billingInfo?: BillingInfo | null;
  subscriptionSeats: SubscriptionSeat[];
  currentSubscriptionSeatWithBillingPeriod?: SubscriptionSeatWithBillingPeriod;
}

export function ProSubscription({
  billingInfoUrl,
  accountUrl,
  license,
  onCancelSubscription,
  billingInfo,
  currentSubscriptionSeatWithBillingPeriod,
  onLicenseChange,
}: Props) {
  const cancelSubscription = () => {
    if (onCancelSubscription && license) {
      onCancelSubscription(license);
    }
  };
  const companyName = currentSubscriptionSeatWithBillingPeriod?.companyName;
  const isCanceled = currentSubscriptionSeatWithBillingPeriod?.recurlySubscriptionState === "canceled";
  const isProTrial = license?.type === LicenseType.proTrial;
  const changeButtonLabel = isCanceled && !isProTrial ? "Reactivate Subscription" : "Change Lens Subscription";
  const paymentMethod = isProTrial ? "No Payment Method Required" : billingInfo?.paymentMethod?.cardType;
  const billingPeriodLabel = getBillingPeriodLabel(license, currentSubscriptionSeatWithBillingPeriod, isCanceled);
  const billingPeriodEndsLabel = getBillingPeriodEndsLabel(
    license,
    currentSubscriptionSeatWithBillingPeriod,
    isCanceled,
  );
  const licenseType = useMemo(() => {
    if (license?.type === LicenseType.proTrial) {
      return "proTrial";
    }

    if (license?.type === LicenseType.pro || license?.type === LicenseType.proProd) {
      return "pro";
    }
  }, [license?.type]);

  const billing = (
    <div className={styles.billing}>
      <>
        <ul>
          <li>
            <CreditCardIcon size="sm" />
            {currentSubscriptionSeatWithBillingPeriod?.isBusinessAccount && companyName ? (
              <span>
                Paid By <span className={styles.companyName}>{companyName}</span>
              </span>
            ) : (
              paymentMethod
            )}
          </li>
          {currentSubscriptionSeatWithBillingPeriod?.billingPeriod && (
            <li>
              <RepeatIcon size="sm" />
              {billingPeriodLabel}
            </li>
          )}
          {currentSubscriptionSeatWithBillingPeriod?.currentPeriodEndsAt && (
            <li>
              <EventIcon size="sm" />
              {billingPeriodEndsLabel}:{" "}
              {dateToString(new Date(currentSubscriptionSeatWithBillingPeriod.currentPeriodEndsAt))}
            </li>
          )}
          {billingInfoUrl && license?.type !== LicenseType.proTrial && (
            <li>
              <TrackedAnchor className={styles.billingLink} href={billingInfoUrl}>
                Billing details
              </TrackedAnchor>
            </li>
          )}
        </ul>
      </>
      <div className={styles.buttons}>
        {isProTrial ? null : (
          <Button
            label="Cancel Subscription"
            primary
            discreet
            onClick={() => cancelSubscription()}
            disabled={!accountUrl || isCanceled}
          />
        )}
        {(isCanceled || isProTrial) && (
          <Button
            label={changeButtonLabel}
            primary
            onClick={() => {
              if (onLicenseChange) {
                onLicenseChange();
              }
            }}
          />
        )}
      </div>
    </div>
  );

  return licenseType ? (
    <ActiveLensSubscription licenseType={licenseType} features={planMetadata.pro.features} billing={billing} />
  ) : null;
}
